import { getToken, clearToken } from '../utils/authToken';
import { useState, useRef, useEffect } from 'react';
import { API_URL, API } from '../config';
import { User, Mail, Lock, Camera, X, Save, Eye, EyeOff, Smile, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';


const UserProfile = ({ isLoggedIn, externalOpen, onExternalClose }) => {
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [communicationStyle, setCommunicationStyle] = useState(
    localStorage.getItem('damyxia_style') || 'auto'
  );
  const [explanationLevel, setExplanationLevel] = useState(
    localStorage.getItem('damyxia_level') || 'auto'
  );
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [photo, setPhoto] = useState('');
  const [email, setEmail] = useState(localStorage.getItem('userEmail') || '');
  
  useEffect(() => {
    if (externalOpen) {
      setIsOpen(true);
    }
  }, [externalOpen]);

  // Always fetch email and avatar from API when profile opens
  useEffect(() => {
    if (isOpen) {
      const token = getToken();
      if (token && token !== 'guest') {
        fetch(`${API}/auth/me`, {
          credentials: 'include',
        })
          .then(res => res.ok ? res.json() : null)
          .then(data => {
            if (data?.email) {
              setEmail(data.email);
              localStorage.setItem('userEmail', data.email);
            }
            if (data?.avatar) {
              setPhoto(data.avatar);
              try { localStorage.setItem('userPhoto', data.avatar); } catch {}
            }
          })
          .catch(() => {});
      }
    }
  }, [isOpen]);
  
  const handleClose = () => {
    setIsOpen(false);
    setIsEditing(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    if (onExternalClose) {
      onExternalClose();
    }
  };

  const txt = {
    es: {
      myProfile: 'Mi Perfil',
      email: 'Correo electrónico',
      notAvailable: 'No disponible',
      currentPassword: 'Contraseña actual',
      newPassword: 'Nueva contraseña',
      confirmPassword: 'Confirmar contraseña',
      password: 'Contraseña',
      changePhoto: 'Clic para cambiar foto',
      imageTooLarge: 'La imagen debe ser menor a 2MB',
      photoUpdated: 'Foto actualizada',
      loginRequired: 'Debes iniciar sesión para editar tu perfil',
      passwordsNotMatch: 'Las contraseñas no coinciden',
      passwordMinLength: 'La contraseña debe tener al menos 6 caracteres',
      passwordUpdated: 'Contraseña actualizada',
      errorUpdate: 'Error al actualizar perfil',
      cancel: 'Cancelar',
      saving: 'Guardando...',
      save: 'Guardar',
      editPassword: 'Cambiar Contraseña',
      stylePreferences: 'Preferencias de Estilo',
      communicationStyle: 'Estilo de comunicación',
      styleAuto: 'Auto',
      styleCasual: 'Casual',
      styleProfessional: 'Profesional',
      tooltipStyleAuto: 'DamyxIA adapta el tono automáticamente según el contexto',
      tooltipStyleCasual: 'Tono relajado y amigable, como hablar con un amigo',
      tooltipStyleProfessional: 'Tono formal y estructurado, ideal para trabajo',
      explanationLevel: 'Nivel de explicación',
      levelAuto: 'Auto',
      levelBasic: 'Básico',
      levelIntermediate: 'Intermedio',
      levelAdvanced: 'Avanzado',
      tooltipAuto: 'DamyxIA adapta automáticamente el nivel de detalle según tu pregunta',
      tooltipBasic: 'Respuestas simples y directas, fáciles de entender',
      tooltipIntermediate: 'Explicaciones con detalle moderado y ejemplos prácticos',
      tooltipAdvanced: 'Análisis profundo con terminología técnica y detalles completos',
      preferencesSaved: 'Preferencias guardadas',
    },
    en: {
      myProfile: 'My Profile',
      email: 'Email',
      notAvailable: 'Not available',
      currentPassword: 'Current password',
      newPassword: 'New password',
      confirmPassword: 'Confirm password',
      password: 'Password',
      changePhoto: 'Click to change photo',
      imageTooLarge: 'Image must be less than 2MB',
      photoUpdated: 'Photo updated',
      loginRequired: 'You must sign in to edit your profile',
      passwordsNotMatch: 'Passwords do not match',
      passwordMinLength: 'Password must be at least 6 characters',
      passwordUpdated: 'Password updated',
      errorUpdate: 'Error updating profile',
      cancel: 'Cancel',
      saving: 'Saving...',
      save: 'Save',
      editPassword: 'Change Password',
      stylePreferences: 'Style Preferences',
      communicationStyle: 'Communication style',
      styleAuto: 'Auto',
      styleCasual: 'Casual',
      styleProfessional: 'Professional',
      tooltipStyleAuto: 'DamyxIA automatically adapts the tone based on context',
      tooltipStyleCasual: 'Relaxed and friendly tone, like talking to a friend',
      tooltipStyleProfessional: 'Formal and structured tone, ideal for work',
      explanationLevel: 'Explanation level',
      levelAuto: 'Auto',
      levelBasic: 'Basic',
      levelIntermediate: 'Intermediate',
      levelAdvanced: 'Advanced',
      tooltipAuto: 'DamyxIA automatically adapts the detail level based on your question',
      tooltipBasic: 'Simple and direct answers, easy to understand',
      tooltipIntermediate: 'Moderate detail with practical examples',
      tooltipAdvanced: 'Deep analysis with technical terminology and full details',
      preferencesSaved: 'Preferences saved',
    }
  };
  const t = txt[language] || txt.es;

  const handleStyleChange = (style) => {
    setCommunicationStyle(style);
    localStorage.setItem('damyxia_style', style);
    toast.success(t.preferencesSaved);
  };

  const handleLevelChange = (level) => {
    setExplanationLevel(level);
    localStorage.setItem('damyxia_level', level);
    toast.success(t.preferencesSaved);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error(t.imageTooLarge);
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = async () => {
        // Resize to max 200x200 and compress to JPEG
        const canvas = document.createElement('canvas');
        const MAX = 200;
        let w = img.width, h = img.height;
        if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
        else { w = Math.round(w * MAX / h); h = MAX; }
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        const compressed = canvas.toDataURL('image/jpeg', 0.7);
        setPhoto(compressed);
        try {
          localStorage.setItem('userPhoto', compressed);
        } catch (e) {
          // Quota exceeded
        }
        // Guardar en backend (persistente)
        try {
          const token = getToken();
          if (token && token !== 'guest') {
            await axios.put(`${API}/auth/profile-photo`, 
              { avatar: compressed },
              {}
            );
          }
        } catch (err) {
          // Silently fail backend save, photo still visible locally
        }
        toast.success(t.photoUpdated);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleSaveProfile = async () => {
    setIsLoading(true);
    try {
      const token = getToken();
      if (!token || token === 'guest') {
        toast.error(t.loginRequired);
        return;
      }

      if (newPassword) {
        if (newPassword !== confirmPassword) {
          toast.error(t.passwordsNotMatch);
          setIsLoading(false);
          return;
        }
        if (newPassword.length < 6) {
          toast.error(t.passwordMinLength);
          setIsLoading(false);
          return;
        }

        await axios.put(
          `${API}/auth/update-password`,
          {
            current_password: currentPassword,
            new_password: newPassword
          },
          {}
        );
        
        toast.success(t.passwordUpdated);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }

      setIsEditing(false);
    } catch (error) {
      const msg = error.response?.data?.detail || t.errorUpdate;
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isLoggedIn) return null;

  // JSX compartido para el contenido del perfil
  const profileContentJSX = (
    <>
      {/* Avatar */}
      <div className="flex flex-col items-center mb-4">
        <div className="relative">
          <div className="w-24 h-24 rounded-full bg-zinc-800 border-2 border-zinc-700 flex items-center justify-center overflow-hidden">
            {photo ? (
              <img 
                key={photo}
                src={photo} 
                alt="" 
                className="w-full h-full object-cover" 
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <User className="w-10 h-10 text-zinc-500" />
            )}
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="absolute bottom-0 right-0 p-2 bg-indigo-600 hover:bg-indigo-700 rounded-full text-white transition-colors"
            title={t.changePhoto}
          >
            <Camera className="w-4 h-4" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className="hidden"
          />
        </div>
        <p className="mt-2 text-xs text-zinc-500">{t.changePhoto}</p>
      </div>

      {/* Email */}
      <div className="mb-4">
        <label className="flex items-center gap-2 text-xs text-zinc-500 mb-1.5">
          <Mail className="w-3.5 h-3.5" />
          {t.email}
        </label>
        <div className="px-3 py-2.5 bg-zinc-800/50 border border-zinc-700/50 rounded-lg text-sm text-zinc-300">
          {email || t.notAvailable}
        </div>
      </div>

      {/* Style Preferences */}
      <div className="pt-3 border-t border-zinc-800/50 mb-4">
        <h4 className="text-xs font-medium text-zinc-400 mb-3 flex items-center gap-2">
          <Smile className="w-3.5 h-3.5" />
          {t.stylePreferences}
        </h4>
        
        <div className="mb-3">
          <label className="text-xs text-zinc-500 mb-2 block">{t.communicationStyle}</label>
          <div className="grid grid-cols-3 gap-1.5">
            {[
              {style: 'auto', emoji: '🔄', tooltip: 'tooltipStyleAuto'},
              {style: 'casual', emoji: '😊', tooltip: 'tooltipStyleCasual'},
              {style: 'professional', emoji: '👔', tooltip: 'tooltipStyleProfessional'}
            ].map(({style, emoji, tooltip}) => (
              <div key={style} className="relative group">
                <button
                  onClick={() => handleStyleChange(style)}
                  data-testid={`style-btn-${style}`}
                  className={`w-full px-2 py-2 rounded-lg text-xs font-medium transition-all ${
                    communicationStyle === style
                      ? style === 'auto' ? 'bg-indigo-600 text-white' 
                        : style === 'casual' ? 'bg-green-600 text-white' 
                        : 'bg-blue-600 text-white'
                      : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  {emoji} {t[`style${style.charAt(0).toUpperCase() + style.slice(1)}`]}
                </button>
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-52 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-xs shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[60] pointer-events-none">
                  <p className="font-semibold text-white mb-0.5">{emoji} {t[`style${style.charAt(0).toUpperCase() + style.slice(1)}`]}</p>
                  <p className="text-zinc-300">{t[tooltip]}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs text-zinc-500 mb-2 block flex items-center gap-1.5">
            <GraduationCap className="w-3 h-3" />
            {t.explanationLevel}
          </label>
          <div className="grid grid-cols-2 gap-1.5">
            {[
              {key: 'auto', tKey: 'Auto', emoji: '🔄', color: 'indigo', tooltip: 'tooltipAuto'}, 
              {key: 'basico', tKey: 'Basic', emoji: '🌱', color: 'emerald', tooltip: 'tooltipBasic'}, 
              {key: 'intermedio', tKey: 'Intermediate', emoji: '📚', color: 'amber', tooltip: 'tooltipIntermediate'}, 
              {key: 'avanzado', tKey: 'Advanced', emoji: '🎓', color: 'purple', tooltip: 'tooltipAdvanced'}
            ].map(({key, tKey, emoji, color, tooltip}) => (
              <div key={key} className="relative group">
                <button
                  onClick={() => handleLevelChange(key)}
                  data-testid={`level-btn-${key}`}
                  className={`w-full px-2 py-2 rounded-lg text-xs font-medium transition-all ${
                    explanationLevel === key
                      ? `bg-${color}-600 text-white`
                      : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  {emoji} {t[`level${tKey}`]}
                </button>
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-52 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-xs shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[60] pointer-events-none">
                  <p className="font-semibold text-white mb-0.5">{emoji} {t[`level${tKey}`]}</p>
                  <p className="text-zinc-300">{t[tooltip]}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Password Section */}
      <div className="pt-3 border-t border-zinc-800/50">
        {isEditing ? (
          <div className="space-y-3">
            {/* Contraseña Actual */}
            <div>
              <label className="flex items-center gap-2 text-xs text-zinc-500 mb-1.5">
                <Lock className="w-3.5 h-3.5" />
                {t.currentPassword}
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-3 py-2 pr-10 bg-zinc-800/50 border border-zinc-700/50 rounded-lg text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-500 hover:text-zinc-300 transition-colors"
                  tabIndex={-1}
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Nueva Contraseña */}
            <div>
              <label className="flex items-center gap-2 text-xs text-zinc-500 mb-1.5">
                <Lock className="w-3.5 h-3.5" />
                {t.newPassword}
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 pr-10 bg-zinc-800/50 border border-zinc-700/50 rounded-lg text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-500 hover:text-zinc-300 transition-colors"
                  tabIndex={-1}
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Confirmar Contraseña */}
            <div>
              <label className="flex items-center gap-2 text-xs text-zinc-500 mb-1.5">
                <Lock className="w-3.5 h-3.5" />
                {t.confirmPassword}
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 pr-10 bg-zinc-800/50 border border-zinc-700/50 rounded-lg text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-500 hover:text-zinc-300 transition-colors"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <label className="flex items-center gap-2 text-xs text-zinc-500 mb-1.5">
              <Lock className="w-3.5 h-3.5" />
              {t.password}
            </label>
            <div className="px-3 py-2.5 bg-zinc-800/50 border border-zinc-700/50 rounded-lg text-sm text-zinc-400">
              ••••••••••••
            </div>
          </div>
        )}
      </div>
    </>
  );

  // Botones de acción
  const actionButtonsJSX = (
    <div className="flex gap-2">
      {isEditing ? (
        <>
          <button
            onClick={() => {
              setIsEditing(false);
              setCurrentPassword('');
              setNewPassword('');
              setConfirmPassword('');
            }}
            className="flex-1 px-4 py-2.5 text-sm text-zinc-400 hover:text-zinc-200 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
          >
            {t.cancel}
          </button>
          <button
            onClick={handleSaveProfile}
            disabled={isLoading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {isLoading ? t.saving : t.save}
          </button>
        </>
      ) : (
        <button
          onClick={() => setIsEditing(true)}
          className="w-full px-4 py-2.5 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors font-medium"
          data-testid="edit-password-btn"
        >
          {t.editPassword}
        </button>
      )}
    </div>
  );

  // Si se abre externamente (desde Settings), mostrar como modal centrado
  if (externalOpen) {
    return (
      <>
        <div 
          className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm" 
          onClick={handleClose} 
        />
        
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between bg-gradient-to-r from-zinc-800 to-zinc-900">
              <h3 className="font-semibold text-lg text-white">{t.myProfile}</h3>
              <button
                onClick={handleClose}
                className="p-1.5 hover:bg-zinc-700 rounded-lg transition-colors"
                data-testid="close-profile-modal"
              >
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 max-h-[70vh] overflow-y-auto">
              {profileContentJSX}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-zinc-800 bg-zinc-800/30">
              {actionButtonsJSX}
            </div>
          </div>
        </div>
      </>
    );
  }

  // Modo normal - botón de avatar con dropdown
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-9 h-9 rounded-full bg-zinc-800/50 border border-zinc-700/50 flex items-center justify-center overflow-hidden hover:border-indigo-500/50 transition-colors"
        title={t.myProfile}
        data-testid="profile-btn"
      >
        {photo ? (
          <img src={photo} alt="Profile" className="w-full h-full object-cover" />
        ) : (
          <User className="w-4 h-4 text-zinc-400" />
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={handleClose} />
          
          <div className="absolute right-0 top-12 w-80 bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl z-50 overflow-hidden">
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="font-medium text-zinc-200">{t.myProfile}</h3>
              <button
                onClick={handleClose}
                className="p-1 hover:bg-zinc-800 rounded transition-colors"
              >
                <X className="w-4 h-4 text-zinc-500" />
              </button>
            </div>

            <div className="p-4">
              {profileContentJSX}
            </div>

            <div className="p-4 border-t border-zinc-800">
              {actionButtonsJSX}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserProfile;
